import type { Ref } from 'vue';
import { useUrlSync } from 'shared/composables/useUrlSync';
import type { FacetGroupModel, FacetItem } from '../DocumentSearch.types';
import { FACET_KEYS } from '../DocumentSearch.types';

type Options = {
  keyword: Ref<string>;
  filters: Ref<FacetGroupModel[]>;
  searchArchived: boolean;
  facetKeys?: readonly string[];
  onChangeFromPopstate?: () => void;
};

const QUERY_PARAM = 'query';
const ARCHIVED_PARAM = 'archived';

function getSelectedValues(group: FacetGroupModel): string[] {
  return (group?.data ?? [])
    .filter(i => i.checked)
    .map(i => i.value ?? i.name)
    .filter(Boolean) as string[];
}

export function useFacetUrlSync(opts: Options) {
  const {
    keyword,
    filters,
    searchArchived,
    onChangeFromPopstate,
  } = opts;

  const facetKeys = Array.isArray(opts.facetKeys) && opts.facetKeys.length
    ? opts.facetKeys
    : FACET_KEYS;

  function parseFiltersFromURL(url: URL): FacetGroupModel[] {
    const groups: FacetGroupModel[] = [];

    for (const key of facetKeys) {
      const raw = url.searchParams.get(key);
      if (!raw) continue;

      const values = raw.split(',').map(s => s.trim()).filter(Boolean);
      if (!values.length) continue;

      const data: FacetItem[] = values.map(v => ({
        name: v,
        value: v,
        checked: true,
        count: 0,
        visible: true,
      }));

      groups.push({ id: key, name: key, data });
    }

    return groups;
  }

  const {
    serializeToURL,
    initFromUrl,
    watchStateEffect,
    registerPopstate,
    unregisterPopstate,
  } = useUrlSync({
    onChangeFromPopstate,
    serialize: () => {
      const entries: Record<string, string | null> = {
        [QUERY_PARAM]: keyword.value?.trim() || null,
      };

      for (const key of facetKeys) {
        const group = filters.value.find(g => g.id === key || g.name === key);
        const selected = group ? getSelectedValues(group) : [];
        entries[key] = selected.length ? selected.join(',') : null;
      }

      entries[ARCHIVED_PARAM] = searchArchived ? '1' : null;

      return entries;
    },
    parse: (url) => {
      keyword.value = url.searchParams.get(QUERY_PARAM) ?? '';
      filters.value = parseFiltersFromURL(url);
    },
  });

  function watchFiltersEffect(effect: () => void, opts?: { flush?: 'pre' | 'post' | 'sync' }) {
    return watchStateEffect(filters, effect, opts);
  }

  return {
    serializeToURL,
    parseFiltersFromURL,
    initFromUrl,
    watchFiltersEffect,
    registerPopstate,
    unregisterPopstate,
  };
}
